import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import ProductSubscribedResponseResource from "common/resources/ProductSubscribed/ProductSubscribedResponseResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import I18n from "components/materials/I18n";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import ProductSubscribedService from "services/ProductSubscribedService";
import { container } from "tsyringe";

const productSubscribedService = container.resolve(ProductSubscribedService);

type IProps = {
	productSubscribed: ProductSubscribedResponseResource;
};

/** Nom du fichier téléchargé : « Dossier complet - Prénom NOM - Produit.zip », sans caractère interdit dans un nom de fichier. */
function getFileName(productSubscribed: ProductSubscribedResponseResource): string {
	const { subscriber, corporationSubscriber, productSheet } = productSubscribed;
	const identity = corporationSubscriber?.raisonSociale ?? (subscriber ? `${subscriber.firstName} ${subscriber.lastName.toUpperCase()}` : null);
	const name = [I18n.asset.pages.product.complete_dossier.file_name, identity, productSheet?.name].filter(Boolean).join(" - ");
	return `${name
		.replace(/[\\/:*?"<>|]+/g, "-")
		.replace(/\s+/g, " ")
		.trim()}.zip`;
}

/**
 * Private equity : en fin de tunnel, le conseiller exporte le dossier complet (documents remplis dans la version qui partira en
 * signature, sans les cadres de l'aperçu, et toutes les pièces déposées) pour le faire relire par la société de gestion.
 * Si un document ne se génère pas ou si une pièce manque, rien n'est téléchargé : le message dit pourquoi.
 */
export default function DownloadCompleteDossierButton({ productSubscribed }: IProps) {
	const [isDownloading, setIsDownloading] = useState(false);

	const download = useCallback(async () => {
		setIsDownloading(true);
		try {
			const content = await productSubscribedService.getCompleteDossierZip(productSubscribed.id);
			const url = URL.createObjectURL(content);
			const link = document.createElement("a");
			link.href = url;
			link.download = getFileName(productSubscribed);
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			// Seul un motif écrit par le serveur (pièce introuvable, document non généré) s'affiche tel quel ; une panne, jamais.
			const reason = Array.isArray(error) ? (error as ValidationError[])[0]?.constraints?.["error"] : undefined;
			toast.error(typeof reason === "string" ? reason : I18n.asset.pages.product.complete_dossier.error);
		} finally {
			setIsDownloading(false);
		}
	}, [productSubscribed]);

	return (
		<Button variant={EButtonVariant.OUTLINED} onClick={download} icon={<ArrowDownTrayIcon />} loading={isDownloading} disabled={isDownloading}>
			{I18n.asset.pages.product.complete_dossier.download}
		</Button>
	);
}
